import React, { useContext, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { linkPropType } from '../../utils/props';

import './Lightbox.css';
import { getThemeClassName, ThemeContext } from '../../utils/theme';

// Persistent portal container - created once, reused across all lightbox instances
var portalContainer = null;
var getPortalContainer = function(themeClassName) {
    if (!portalContainer) {
        portalContainer = document.createElement('div');
        portalContainer.className = [
            'auctionlook-lightbox-portal',
            'al_App',
            'al_container',
            themeClassName
        ].join(' ');
        document.body.appendChild(portalContainer);
    }
    return portalContainer;
};

var Lightbox = function(props) {
    var imageSrc = props.imageSrc;
    var imageAlt = props.imageAlt;
    var closeUrl = props.closeUrl;
    var nextImage = props.nextImage;
    var previousImage = props.previousImage;

    var navigate = useNavigate();
    var theme = useContext(ThemeContext);
    var themeClassName = getThemeClassName(theme);

    // Keyboard navigation
    var handleKeyDown = useCallback(function(e) {
        if (e.key === 'Escape' && closeUrl) {
            e.preventDefault();
            navigate(closeUrl, { replace: true });
        } else if (e.key === 'ArrowLeft' && previousImage) {
            e.preventDefault();
            navigate(previousImage.url, { replace: true });
        } else if (e.key === 'ArrowRight' && nextImage) {
            e.preventDefault();
            navigate(nextImage.url, { replace: true });
        }
    }, [closeUrl, nextImage, previousImage, navigate]);

    // Setup body scroll lock and keyboard listener
    useEffect(function() {
        var originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        return function() {
            document.body.style.overflow = originalOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    // Navigation handlers - use replace to avoid remounting
    var handleClose = function(e) {
        e.preventDefault();
        navigate(closeUrl, { replace: true });
    };

    var handlePrev = function(e) {
        e.preventDefault();
        if (previousImage) {
            navigate(previousImage.url, { replace: true });
        }
    };

    var handleNext = function(e) {
        e.preventDefault();
        if (nextImage) {
            navigate(nextImage.url, { replace: true });
        }
    };

    var container = getPortalContainer(themeClassName);

    return createPortal(
        React.createElement('div', { className: 'al-lightbox', role: 'dialog', 'aria-modal': 'true' },
            // Solid black backdrop - click to close
            React.createElement('div', {
                className: 'al-lightbox-overlay',
                onClick: handleClose,
                role: 'button',
                tabIndex: -1,
                'aria-label': 'Close lightbox'
            }),

            // Main image container
            React.createElement('div', { className: 'al-lightbox-content' },
                imageSrc && React.createElement('img', {
                    src: imageSrc,
                    alt: imageAlt,
                    className: 'al-lightbox-image',
                    draggable: false
                })
            ),

            // Close button
            React.createElement('button', {
                className: 'al-lightbox-close',
                onClick: handleClose,
                'aria-label': 'Close',
                type: 'button'
            },
                React.createElement(X, { size: 28 })
            ),

            // Previous
            previousImage && React.createElement('button', {
                className: 'al-lightbox-nav al-lightbox-nav--prev',
                onClick: handlePrev,
                'aria-label': 'Previous image',
                type: 'button'
            },
                React.createElement(ChevronLeft, { size: 40 })
            ),

            // Next
            nextImage && React.createElement('button', {
                className: 'al-lightbox-nav al-lightbox-nav--next',
                onClick: handleNext,
                'aria-label': 'Next image',
                type: 'button'
            },
                React.createElement(ChevronRight, { size: 40 })
            )
        ),
        container
    );
};

Lightbox.propTypes = {
    imageSrc: PropTypes.string,
    imageAlt: PropTypes.string.isRequired,
    closeUrl: PropTypes.string,
    nextImage: linkPropType,
    previousImage: linkPropType
};

export default Lightbox;
